import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { userAppContext } from '../../../../../context/Context';
import { useMappingDrillDown } from '../../../../../context/MappingDrillDownContext';
import { useEnrichmentState } from '../../../../../hooks/useEnrichmentState';
import { trackEnrichmentAction } from '../../../../../utils/analytics';

import { EVIDENCE_LEGEND, restrictedChemicalColumns, chemicalRestrictedColumns } from '../../constants';

import RenderInfoLogic from '../../../../../components/InfoSidebar/RenderInfoLogic';
import updateParams from '../../../../../utils/updateParams';
import { removePanelFiltersFromSearchParams } from '../../../../../utils/encodeUtils';
import { getAllColumns } from '../../../../../components/Table/columns/index';
import { ColumnVisibilityModels } from '../../../../../components/Table/ColumnVisibilityModels';
import {
  isRunEnrichmentEnabled,
  dedupeRowsByEnrichmentLogicalId,
  getUniqueEnrichmentExportIds,
} from '../../../../../components/Table/utils/index';

import SharedButton from '../../../../../global/Button/Button';
import { UserContext } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';
import Alert from '../../../../../global/Alert/Alert';
import { isSourceEnabledForTab } from '../../helpers/sourceListHelpers';
import { EvidenceParams, evidenceSummaryRequest } from '../../helpers/tableHelpers';
import { deleteFilter } from '../../../../../services/savedFiltersService';

// Hooks
import { useColumnManagement } from './hooks/useColumnManagement';
import { useAlertSystem } from './hooks/useAlertSystem';
import { useSaveOperations } from './hooks/useSaveOperations';
import { useConfigurationLoader } from './hooks/useConfigurationLoader';
import { useDownloadOperations } from './hooks/useDownloadOperations';
import { useSummaryMatch } from './hooks/useSummaryMatch';
import { hasActiveColumnFilters } from '../../utils/activeColumnFilters';
import { ENTITY_PINNABLE_COLUMNS, getEntityColumnsToPin } from '../../utils/entityColumnDefaults';

// Components
import { SaveSearchModal } from './components/SaveSearchModal';
import { SaveColumnsModal } from './components/SaveColumnsModal';
import { SaveScoreModal } from './components/SaveScoreModal';
import { GridToolbar } from './components/GridToolbar';
import { AcademicRestrictions } from './components/AcademicRestrictions';
import { GridContainer } from './components/GridContainer';
import { RightAsideSidebar } from './components/RightAsideSidebar';
import { useRightSidebarLayout } from '../../context/RightSidebarLayoutContext';
import { SearchGridHelpContext } from '../../context/SearchGridHelpContext';
import SearchGridHelpModal from '../../../../../components/About/AboutPopups';
import { useTableStore } from '@/store/tableStore';

// Types
import { ManageGridProps, SearchItem } from './types';

import './EnrichmentSelectionBar.css';
import SharedBadge from '@/global/Badge/Badge';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import '@/components/Modal/RestrictedAccess.css';
import { MappedGeneInfoButton } from '@/components/Mapping/MappedGeneInfoButton';

function SummaryEvidenceSegmentedControl({
  showSummary,
  showEvidence,
  active,
  onChange,
}: {
  showSummary: boolean;
  showEvidence: boolean;
  active: 'summary' | 'evidence';
  onChange: (next: 'summary' | 'evidence') => void;
}) {
  if (showSummary && showEvidence) {
    return (
      <div
        role="tablist"
        aria-label="Table view"
        className="inline-flex h-8 shrink-0 items-stretch rounded-3xl border border-slate-200 bg-slate-100/90 p-0.5 shadow-sm"
      >
        <button
          type="button"
          role="tab"
          aria-selected={active === 'summary'}
          className={`rounded-3xl px-3 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-1 ${
            active === 'summary' ? 'bg-white text-slate-900 shadow-lg' : 'text-slate-600 hover:text-slate-900'
          }`}
          onClick={() => onChange('summary')}
        >
          Summary
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={active === 'evidence'}
          className={`rounded-3xl px-3 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-1 ${
            active === 'evidence'
              ? 'bg-white text-slate-900 shadow-lg'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          onClick={() => onChange('evidence')}
        >
          Evidence
        </button>
      </div>
    );
  }
  if (showSummary && !showEvidence) {
    return (
      <span className="inline-flex h-8 items-center rounded-3xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">
        Summary
      </span>
    );
  }
  if (!showSummary && showEvidence) {
    return (
      <span className="inline-flex h-8 items-center rounded-3xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">
        Evidence
      </span>
    );
  }
  return null;
}

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';
const SHOW_HISTORIAL_SCORE = process.env.REACT_APP_SHOW_HISTORIAL_SCORE === 'true';

function extractHeaderText(node: any): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node).trim();
  if (Array.isArray(node)) return node.map(extractHeaderText).filter(Boolean).join(' ').trim();
  if (React.isValidElement(node)) {
    return extractHeaderText((node.props as any)?.children);
  }
  return '';
}

function ManageGrid({
  isMapping,
  mappingContent,
  restrictChemical,
  view,
  tab,
  panelTab,
  isEvidence,
  source,
  searchItems,
  setSearchItems,
  isInfoSidebarOpen,
  setIsInfoSidebarOpen,
  dispatch,
  defaultFilterValue,
  sourceWeights = [],
  onOpenSearchItemInfo,
  columnVisibilityModel: controlledColumnVisibilityModel,
  setColumnVisibilityModel: controlledSetColumnVisibilityModel,
  columnOrder: controlledColumnOrder,
  setColumnOrder: controlledSetColumnOrder,
  columnPinning: controlledColumnPinning,
  setColumnPinning: controlledSetColumnPinning,
  onColumnStateInit,
  showRightSidebar = true,
  resetRef,
  summaryEvidenceToggle,
  showSummaryMatch = false,
}: ManageGridProps) {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const urlIdents = urlSearchParams.get('idents') || '';
  const urlView = urlSearchParams.get('view') || '';
  const hasSpecificIdents = (urlIdents || 'ALL').toUpperCase() !== 'ALL';

  const pinOptions = { view, urlView, tab: String(panelTab ?? tab ?? '') };
  const columnsToPinByDefault = (model: Record<string, boolean> | null | undefined) =>
    getEntityColumnsToPin(model, pinOptions);

  const sourceCurated = [
    'CURATED',
    'CLINGEN',
    'CLINPGX',
    'CLINVAR',
    'GENCC',
    'MGD_HUMAN',
    'ORPHANET',
    'PSYGENET',
    'RGD_HUMAN',
    'UNIPROT',
    'GWASCAT',
    'PHEWASCAT',
  ];
  const { authorization, isAuthenticated, selectedInfoItem, restrictSources, user } =
    userAppContext() as unknown as UserContext;

  const { effectiveTab } = useMappingDrillDown();
  const columnsTab =
    isMapping && typeof effectiveTab === 'string' && effectiveTab.length > 0
      ? effectiveTab
      : isMapping && typeof mappingContent?.tabSegment === 'string' && mappingContent.tabSegment.length > 0
        ? mappingContent.tabSegment.toUpperCase()
        : tab;
  const allColumns = getAllColumns({ isEvidence, tab: columnsTab });
  const downloadColumnLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    allColumns.forEach(col => {
      if (!col?.name) return;
      const label = extractHeaderText(col.header);
      labels[col.name] = label || col.name;
    });
    return labels;
  }, [allColumns]);

  const savedColumsApplied = useTableStore(s => s.savedColumsApplied);

  const [totalResults, setTotalResults] = useState(0);
  const getFilterValueRef = React.useRef<any>(null);

  const getReferenceTypeFromFilters = useCallback((filters: any[]): string => {
    const rtFilter = filters?.find((f: any) => f.name === 'reference_type');
    const rtValue = Array.isArray(rtFilter?.value) ? (rtFilter.value[0] ?? 'ALL') : rtFilter?.value || 'ALL';
    return rtValue || 'ALL';
  }, []);

  const [activeReferenceType, setActiveReferenceType] = useState<string>(() =>
    getReferenceTypeFromFilters(defaultFilterValue ?? []),
  );
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(() =>
    hasActiveColumnFilters(defaultFilterValue ?? []),
  );

  useEffect(() => {
    setHasActiveFilters(hasActiveColumnFilters(defaultFilterValue ?? []));
  }, [defaultFilterValue]);

  const handleFiltersChange = useCallback(
    (filters: any[]) => {
      const rtValue = getReferenceTypeFromFilters(filters);
      setActiveReferenceType(prev => (prev === rtValue ? prev : rtValue));
      setHasActiveFilters(hasActiveColumnFilters(filters));
    },
    [getReferenceTypeFromFilters],
  );

  const internalColumnManagement = useColumnManagement({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
    restrictChemical,
    searchItems,
  });
  const reportedInitRef = useRef(false);

  useEffect(() => {
    reportedInitRef.current = false;
  }, [tab, isEvidence]);

  const columnVisibilityModel =
    controlledColumnVisibilityModel !== undefined
      ? controlledColumnVisibilityModel
      : internalColumnManagement.columnVisibilityModel;
  const setColumnVisibilityModel =
    controlledSetColumnVisibilityModel ?? internalColumnManagement.setColumnVisibilityModel;
  const columnOrder =
    controlledColumnOrder !== undefined ? controlledColumnOrder : internalColumnManagement.columnOrder;
  const setColumnOrder = controlledSetColumnOrder ?? internalColumnManagement.setColumnOrder;
  const [internalColumnPinning, setInternalColumnPinning] = useState<{ left?: string[]; right?: string[] }>({
    left: columnsToPinByDefault(columnVisibilityModel),
    right: ['coreAction'],
  });
  const columnPinning = controlledColumnPinning ?? internalColumnPinning;
  const setColumnPinning = controlledSetColumnPinning ?? setInternalColumnPinning;
  const hasLoadedFromSavedRef = internalColumnManagement.hasLoadedFromSavedRef;

  const internalColumnVisibilityModel = internalColumnManagement.columnVisibilityModel;
  const isControlledColumnState = controlledColumnVisibilityModel !== undefined;

  const setColumnVisibilityModelStableRef = useRef(setColumnVisibilityModel);
  setColumnVisibilityModelStableRef.current = setColumnVisibilityModel;
  const setColumnOrderStableRef = useRef(setColumnOrder);
  setColumnOrderStableRef.current = setColumnOrder;
  const columnOrderRef = useRef(columnOrder);
  columnOrderRef.current = columnOrder;

  const handleColumnVisibilityModelChange = useCallback((model: Record<string, boolean>) => {
    // coreAction is a grid-only UI column; keep it out of the shared visibility model.
    const { coreAction: _coreAction, ...rest } = model;
    setColumnVisibilityModelStableRef.current(() => rest);
  }, []);

  // Snapshot of the previous internal model to detect per-column diffs.
  const prevInternalModelRef = useRef<Record<string, boolean> | null | undefined>(null);

  // Reset snapshot on tab/evidence-mode changes (controlled model also resets then).
  useEffect(() => {
    prevInternalModelRef.current = null;
  }, [tab, isEvidence]);

  useEffect(() => {
    if (!isControlledColumnState) return;
    if (isMapping) return;
    if (!internalColumnVisibilityModel) return;

    const prevModel = prevInternalModelRef.current;
    prevInternalModelRef.current = internalColumnVisibilityModel;

    const conditionalColumns = [
      'diseaseName',
      'geneSymbol',
      'variantStrID',
      'strID',
      'chemPrefName',
      'chemID',
      'chemIDs',
      'chemsInSentence',
    ];

    if (prevModel === null) {
      const mismatched = conditionalColumns.filter(
        col =>
          internalColumnVisibilityModel[col] !== undefined &&
          columnVisibilityModel?.[col] !== internalColumnVisibilityModel[col],
      );
      if (mismatched.length === 0) return;
      setColumnVisibilityModelStableRef.current(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        mismatched.forEach(col => {
          updated[col] = internalColumnVisibilityModel[col];
        });
        return updated;
      });
      const becomeVisible = mismatched.filter(col => internalColumnVisibilityModel[col] === true);
      const becomeHidden = new Set(mismatched.filter(col => internalColumnVisibilityModel[col] === false));
      const preserved = (columnOrderRef.current ?? []).filter(
        c => !becomeHidden.has(c) && !becomeVisible.includes(c),
      );
      setColumnOrderStableRef.current([...becomeVisible, ...preserved]);
      return;
    }

    const becameVisible: string[] = [];
    const becameHidden: string[] = [];

    conditionalColumns.forEach(col => {
      const prev = prevModel?.[col];
      const next = internalColumnVisibilityModel[col];
      if (next !== undefined && prev !== next) {
        if (next === true) becameVisible.push(col);
        else becameHidden.push(col);
      }
    });

    if (becameVisible.length === 0 && becameHidden.length === 0) return;

    // Apply only the changed conditional columns; leave all other columns untouched.
    setColumnVisibilityModelStableRef.current(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      [...becameVisible, ...becameHidden].forEach(col => {
        updated[col] = internalColumnVisibilityModel[col];
      });
      return updated;
    });

    const changedCols = new Set([...becameVisible, ...becameHidden]);
    const preserved = (columnOrderRef.current ?? []).filter(c => !changedCols.has(c));
    setColumnOrderStableRef.current([...becameVisible, ...preserved]);

    const columnsToPin = columnsToPinByDefault(internalColumnVisibilityModel);
    const prevLeft = columnPinning?.left ?? [];
    const preservedLeft = prevLeft.filter(col => !ENTITY_PINNABLE_COLUMNS.includes(col));
    const nextLeft = [...columnsToPin, ...preservedLeft];
    const nextRight = columnPinning?.right ?? ['coreAction'];
    const hasSameLeft =
      nextLeft.length === prevLeft.length && nextLeft.every((col, index) => prevLeft[index] === col);
    const prevRight = columnPinning?.right ?? [];
    const hasSameRight =
      nextRight.length === prevRight.length && nextRight.every((col, index) => col === prevRight[index]);

    if (!hasSameLeft || !hasSameRight) {
      setColumnPinning({
        left: nextLeft,
        right: nextRight,
      });
    }
  }, [
    internalColumnVisibilityModel,
    isControlledColumnState,
    isMapping,
    columnVisibilityModel,
    setColumnPinning,
    columnPinning,
    view,
    urlView,
    tab,
    panelTab,
  ]);

  const resetToDefault = useCallback(() => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence, searchItems, urlIdents, urlView);
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];
    const defaultModel =
      baseDefaultModel && restrictedList.length > 0
        ? Object.fromEntries(
            Object.entries(baseDefaultModel).filter(([key]) => !restrictedList.includes(key)),
          )
        : baseDefaultModel;
    if (defaultModel) {
      setColumnVisibilityModel(() => defaultModel);
      const defaultColumnOrder = Object.keys(defaultModel).filter(key => defaultModel[key] === true);
      setColumnOrder(defaultColumnOrder);
      const columnsToPin = columnsToPinByDefault(defaultModel as Record<string, boolean>);
      setColumnPinning({
        left: columnsToPin,
        right: columnPinning?.right ?? ['coreAction'],
      });
    }
    hasLoadedFromSavedRef.current = false;
  }, [
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent?.isActive,
    mappingContent?.columVisibilityModelKey,
    restrictChemical,
    searchItems,
    urlIdents,
    urlView,
    setColumnVisibilityModel,
    setColumnOrder,
    setColumnPinning,
    columnPinning?.right,
    hasLoadedFromSavedRef,
    panelTab,
  ]);

  useEffect(() => {
    if (savedColumsApplied) {
      showSuccessAlert(`Column configuration "${savedColumsApplied.name}" applied successfully`);
    }
  }, [savedColumsApplied]);

  useEffect(() => {
    if (onColumnStateInit && controlledColumnVisibilityModel === undefined && !reportedInitRef.current) {
      const baseDefaultModel =
        isMapping && mappingContent?.isActive
          ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
          : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence, searchItems, urlIdents, urlView);
      const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];
      const defaultModel =
        baseDefaultModel && restrictedList.length > 0
          ? Object.fromEntries(
              Object.entries(baseDefaultModel).filter(([key]) => !restrictedList.includes(key)),
            )
          : baseDefaultModel;

      if (defaultModel && Object.keys(defaultModel).length > 0) {
        reportedInitRef.current = true;
        const defaultOrder = Object.keys(defaultModel).filter(key => defaultModel[key] === true);
        onColumnStateInit(isEvidence, defaultModel, defaultOrder);
      }
    }
  }, [
    onColumnStateInit,
    controlledColumnVisibilityModel,
    isEvidence,
    isMapping,
    mappingContent?.isActive,
    mappingContent?.columVisibilityModelKey,
    view,
    tab,
    searchItems,
    urlIdents,
    urlView,
    restrictChemical,
  ]);

  useEffect(() => {
    if (resetRef) {
      resetRef.current = resetToDefault;
    }
  }, [resetRef, resetToDefault]);

  useEffect(() => {
    if (isEvidence) return;
    const showEvidenceTimeline = source !== 'CHEMBL';
    setColumnVisibilityModel((prev: Record<string, boolean>) => {
      if (!prev || !('evidenceTimeline' in prev)) return prev;
      if (prev.evidenceTimeline === showEvidenceTimeline) return prev;
      return { ...prev, evidenceTimeline: showEvidenceTimeline };
    });
  }, [source, isEvidence, setColumnVisibilityModel]);

  // Alert system
  const {
    showAlert,
    alertType,
    showSuccessAlert,
    showErrorAlert,
    showWarningAlert,
    hideAlert,
    getAlertMessage,
  } = useAlertSystem();

  const summaryMatch = useSummaryMatch({
    view,
    source,
    tab,
    searchItems,
    isAuthenticated,
    authorization,
    isMapping,
    mappingContent,
    sourceWeights,
    evidenceColumnVisibilityModel: columnVisibilityModel,
    getFilterValueRef,
    dispatch,
    onWarning: showWarningAlert,
    onError: showErrorAlert,
    onMatchComplete: () => summaryEvidenceToggle?.onChange('summary'),
  });

  // Save operations
  const saveOperations = useSaveOperations({
    searchItems,
    view,
    tab,
    source,
    isEvidence,
    totalResults,
    columnVisibilityModel,
    columnOrder,
    columnPinning,
    sourceWeights,
    getFilterValueRef,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
    summaryMatchActive: !isEvidence && summaryMatch.isActive,
    summaryMatchEncodedParams: summaryMatch.matchEncodedParams,
    summaryMatchStats: summaryMatch.stats,
  });

  const handleClearSummaryMatch = useCallback(() => {
    summaryMatch.clearSummaryMatch();
    try {
      sessionStorage.removeItem('disgenet:pendingSummaryMatch');
    } catch {
      // ignore
    }

    setUrlSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('f');
        removePanelFiltersFromSearchParams(next, 'evidence');
        removePanelFiltersFromSearchParams(next, 'summary');
        return next;
      },
      { replace: true },
    );

    summaryEvidenceToggle?.onChange('evidence');
  }, [setUrlSearchParams, summaryEvidenceToggle, summaryMatch.clearSummaryMatch]);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem('disgenet:pendingSummaryMatch');
    } catch {
      return;
    }
    if (!raw) return;
    if (!searchItems?.length || searchItems[0]?.id === 'ALL') return;

    try {
      const pending = JSON.parse(raw);
      const pendingTab = String(pending?.tab || '').toUpperCase();
      if (pendingTab && pendingTab !== String(tab).toUpperCase()) return;
      if (!pending?.encodedParams || Object.keys(pending.encodedParams).length === 0) {
        sessionStorage.removeItem('disgenet:pendingSummaryMatch');
        return;
      }

      summaryMatch.restoreSummaryMatch(pending);
      summaryEvidenceToggle?.onChange('summary');
      sessionStorage.removeItem('disgenet:pendingSummaryMatch');
    } catch {
      try {
        sessionStorage.removeItem('disgenet:pendingSummaryMatch');
      } catch {
        // ignore
      }
    }
  }, [searchItems, tab, summaryMatch.restoreSummaryMatch, summaryEvidenceToggle]);

  // Download operations
  const downloadOperations = useDownloadOperations({
    view,
    source,
    tab,
    searchItems,
    isAuthenticated,
    authorization,
    isEvidence,
    isMapping,
    mappingContent,
    totalResults,
    columnVisibilityModel,
    getFilterValueRef,
    dispatch,
    sourceWeights,
    columnLabels: downloadColumnLabels,
    onWarning: showWarningAlert,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Right aside sidebar (table controls) — context as single source of truth so close works on first click
  const { isSidebarOpen, setSidebarOpen } = useRightSidebarLayout();

  useEffect(() => {
    if (!isSidebarOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isSidebarOpen, setSidebarOpen]);

  // Configuration loader
  useConfigurationLoader({
    setColumnVisibilityModel,
    setColumnOrder,
    setColumnPinning,
    hasLoadedFromSavedRef,
    dispatch,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
    onRestoreSummaryMatch: payload => {
      summaryMatch.restoreSummaryMatch(payload);
      summaryEvidenceToggle?.onChange('summary');
    },
  });

  // Local state
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [searchHelpOpen, setSearchHelpOpen] = useState(false);
  const [searchHelpScrollToSectionId, setSearchHelpScrollToSectionId] = useState<string | undefined>(
    undefined,
  );
  const openSearchHelp = useCallback((sectionId?: string) => {
    setSearchHelpScrollToSectionId(sectionId);
    setSearchHelpOpen(true);
  }, []);
  const searchGridHelpContextValue = useMemo(() => ({ openSearchHelp }), [openSearchHelp]);
  const [areEvidenceSelectedRows, setEvidenceSelectedRows] = useState(false);
  const [evidenceSummParams, setEvidenceSummParams] = useState<EvidenceParams[]>([]);
  const [enrichmentSelectionMode, setEnrichmentSelectionMode] = useState(false);
  const [enrichmentSelectedCount, setEnrichmentSelectedCount] = useState(0);
  const enrichmentGetSelectionRef = useRef<
    | (() => {
        selectedIds: string[];
        selectedTableIds?: string[];
        selectedRows?: any[];
        selectedRowsById?: Record<string, any>;
        rowData: any[];
        getRowId: ((row: any, index: number) => string) | null;
        tab?: string;
      })
    | null
  >(null);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    setEnrichmentFrom,
    tableEnrichmentSelection,
    setTableEnrichmentSelection,
    clearTableEnrichmentSelection,
  } = useEnrichmentState();

  const [enrichmentRestoreSelection, setEnrichmentRestoreSelection] = useState<{
    selectedTableIds: string[];
    selectedRowsById: Record<string, any>;
  } | null>(null);

  /** Same identifier written into the enrichment textarea (must stay in sync with the sticky counter). */
  const enrichmentEntityTab = String(panelTab ?? tab ?? '').toUpperCase();
  const getRowIdForEnrichment = (row: any): string => {
    if (enrichmentEntityTab === 'GENES' || enrichmentEntityTab === 'GDA') {
      return String(
        row.geneSymbol ??
          row.gene_symbol ??
          row.symbol ??
          row.geneNcbiID ??
          row.geneID ??
          row.gene_id ??
          row.geneId ??
          row.ncbiID ??
          row.gene_ncbi_id ??
          row.geneNcbiIds?.[0] ??
          '',
      ).trim();
    }
    if (enrichmentEntityTab === 'VARIANTS' || enrichmentEntityTab === 'VDA') {
      return String(row.strID ?? row.variantStrID ?? row.strId ?? '').trim();
    }
    return '';
  };

  const enrichmentTabs = ['GDA', 'VDA', 'GENES', 'VARIANTS'];
  const canShowEnrichmentSelection = !isEvidence && enrichmentTabs.includes(enrichmentEntityTab);
  const enableEnrichmentSelection = canShowEnrichmentSelection && enrichmentSelectionMode;

  const enrichmentSelectionModeRef = useRef(enrichmentSelectionMode);
  enrichmentSelectionModeRef.current = enrichmentSelectionMode;
  useLayoutEffect(() => {
    enrichmentSelectionModeRef.current = enrichmentSelectionMode;
    if (!enrichmentSelectionMode) {
      setEnrichmentSelectedCount(0);
    }
  }, [enrichmentSelectionMode]);

  useEffect(() => {
    if (!canShowEnrichmentSelection || !tableEnrichmentSelection) return;
    const currentReturnTo = location.pathname + location.search;
    const selectionTab = String(tableEnrichmentSelection.tab || '').toUpperCase();
    const matchesReturn =
      tableEnrichmentSelection.returnTo === currentReturnTo ||
      tableEnrichmentSelection.returnTo.split('?')[0] === location.pathname;
    const matchesTab = !selectionTab || selectionTab === enrichmentEntityTab;
    if (!matchesReturn || !matchesTab) return;

    const tableIds = tableEnrichmentSelection.selectedTableIds ?? [];
    const rowsById = tableEnrichmentSelection.selectedRowsById ?? {};
    enrichmentSelectionModeRef.current = true;
    setEnrichmentSelectionMode(true);
    setEnrichmentSelectedCount(tableEnrichmentSelection.selectedIds?.length ?? 0);
    if (tableIds.length > 0) {
      setEnrichmentRestoreSelection({ selectedTableIds: tableIds, selectedRowsById: rowsById });
    }
  }, [
    canShowEnrichmentSelection,
    tableEnrichmentSelection,
    location.pathname,
    location.search,
    enrichmentEntityTab,
  ]);

  // User permissions
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);
  const isAcademic = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'][0] === 'ROLE_ACADEMIC';
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');

  const isChemicalRestricted = isAuthenticated && isAcademic;

  const shouldShowMessage =
    isChemicalRestricted &&
    (tab === 'GDA' || tab === 'VDA') &&
    Object.keys(columnVisibilityModel).some(key => restrictedChemicalColumns.includes(key));

  // Effects
  useEffect(() => {
    const searchAndMappingItems = searchItems
      ? searchItems.concat((mappingContent?.mappingItems as SearchItem[]) || [])
      : [];
    if (isInfoSidebarOpen && searchItems && !searchItems.find(item => item.id === selectedInfoItem.id)) {
      if (!searchAndMappingItems.find(item => item.id === selectedInfoItem.id)) {
        setIsInfoSidebarOpen(false);
      }
    }
  }, [searchItems, selectedInfoItem, mappingContent?.mappingItems, isInfoSidebarOpen, setIsInfoSidebarOpen]);

  const handleRemoveSearchItem = (index: number) => {
    if (!searchItems?.length) return;
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const sourceParam = searchParams.get('source');
    const tabParam = searchParams.get('tab');
    const remainingSearchItems = searchItems.filter((_, i) => i !== index);
    if (remainingSearchItems.length === 0) {
      navigate('/');
    } else {
      const newParams = updateParams({
        searchItems: remainingSearchItems as any,
        view: viewParam ?? undefined,
        source: sourceParam ?? undefined,
        tab: tabParam ?? undefined,
      });
      navigate({ pathname: location.pathname, search: newParams });
    }
  };

  // Handlers
  const handleDataFromChild = (data: boolean, params: EvidenceParams[] | any[], selectedCount?: number) => {
    const isEnrichmentMode = enrichmentSelectionModeRef.current || enrichmentSelectionMode;
    if (isEnrichmentMode) {
      const rows = Array.isArray(params) ? params : [];
      const count = selectedCount ?? rows.length;
      setEnrichmentSelectedCount(count);
    } else {
      setEvidenceSelectedRows(data);
      setEvidenceSummParams(params as EvidenceParams[]);
    }
  };

  const handleEvidenceSummClick = async () => {
    const content = await evidenceSummaryRequest(evidenceSummParams, dispatch);
    dispatch({
      type: 'SET_EVIDENCE_MODAL_DATA',
      payload: evidenceSummParams,
    });
    await dispatch({ type: 'SET_EVIDENCE_MODAL_CONTENT', payload: content });
    dispatch({ type: 'SET_SHOW_EVIDENCE_MODAL', payload: true });
  };

  const handleRunEnrichmentFromTable = () => {
    const getSelection = enrichmentGetSelectionRef.current;
    if (!getSelection) return;
    const {
      selectedIds,
      selectedTableIds = [],
      selectedRows: persistedRows,
      selectedRowsById = {},
      rowData: gridRowData,
      getRowId: gridGetRowId,
      tab: gridTab,
    } = getSelection();

    let rowsToUse: any[] = Array.isArray(persistedRows) ? persistedRows.filter(Boolean) : [];

    if (rowsToUse.length < 2 && selectedIds?.length && gridRowData?.length) {
      const idSet = new Set(selectedIds);
      const getRowIdFn = gridGetRowId ?? null;
      rowsToUse = getRowIdFn
        ? gridRowData.filter((row: any, index: number) => idSet.has(getRowIdFn(row, index)))
        : [];
      if (rowsToUse.length < 2) {
        const byIndex = selectedIds
          .map((id: string) => {
            const i = parseInt(id, 10);
            return Number.isNaN(i) ? null : gridRowData[i];
          })
          .filter(Boolean);
        if (byIndex.length >= 2) rowsToUse = byIndex;
      }
    }

    const uniqueRows = dedupeRowsByEnrichmentLogicalId(rowsToUse, getRowIdForEnrichment);
    const exportIds = getUniqueEnrichmentExportIds(uniqueRows, getRowIdForEnrichment);
    if (exportIds.length < 2) return;

    const currentTab = String(panelTab ?? tab ?? gridTab ?? '').toUpperCase();
    const isGenes = currentTab === 'GDA' || currentTab === 'GENES';
    const searchType = isGenes ? 'GENES' : 'VARIANTS';
    const selectedFilter = isGenes ? 'geneSymbol' : 'strID';
    const text = exportIds.join('\n');

    trackEnrichmentAction('enrichment_analysis_click', 'summary_table');
    const returnTo = location.pathname + location.search;

    setTableEnrichmentSelection({
      returnTo,
      tab: currentTab,
      selectedIds: exportIds,
      selectedTableIds,
      selectedRowsById,
    });

    const payload = { from: 'summaryTable', searchType, selectedFilter, text, returnTo };
    setEnrichmentFrom(payload);
    setEnrichmentSelectionMode(false);
    setEnrichmentSelectedCount(0);
    setEnrichmentRestoreSelection(null);
    navigate('/enrichment', { state: payload });
  };

  const handleToggleEnrichmentSelectionMode = () => {
    setEnrichmentSelectionMode(prev => {
      const next = !prev;
      enrichmentSelectionModeRef.current = next;
      if (!next) {
        setEnrichmentSelectedCount(0);
        setEnrichmentRestoreSelection(null);
        clearTableEnrichmentSelection();
      }
      return next;
    });
  };

  const handleResetToDefault = () => {
    resetToDefault();
    showSuccessAlert('Columns reset to default');
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      const baseModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

      if (baseModel) {
        const mergedModel = { ...baseModel };
        Object.keys(savedColumn.columnVisibilityModel).forEach(key => {
          if (key in mergedModel) {
            mergedModel[key] = savedColumn.columnVisibilityModel[key];
          }
        });
        setColumnVisibilityModel(() => mergedModel);
      } else {
        setColumnVisibilityModel(() => savedColumn.columnVisibilityModel);
      }
    }

    if (savedColumn.columnOrder) {
      setColumnOrder(savedColumn.columnOrder);
    }
    if (savedColumn.columnPinning) {
      setColumnPinning(savedColumn.columnPinning);
    }

    showSuccessAlert(`Column configuration "${savedColumn.name}" applied successfully`);
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) {
        await deleteFilter(columnId);
      }
      showSuccessAlert('Column configuration deleted successfully');
    } catch (error) {
      console.error('Error deleting column configuration:', error);
      showErrorAlert('Error deleting column configuration');
    }
  };

  // Memoized columns
  const columnsFromVisibility = useMemo(() => {
    if (!columnVisibilityModel) return null;

    const originalModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
    if (!originalModel) return null;

    const orderedColumns = [];
    const orderedKeys = Object.keys(columnVisibilityModel);

    orderedKeys.forEach(key => {
      if (columnVisibilityModel[key] === true) {
        const column = allColumns.find(col => col.name === key);
        if (column && (column.name !== 'sourceScore' || sourceWeights.length > 0)) {
          orderedColumns.push(column);
        }
      }
    });

    return orderedColumns;
  }, [columnVisibilityModel, sourceWeights, tab, view, isEvidence, allColumns]);

  const columnsFilteredByShow = useMemo(
    () =>
      allColumns.filter(col => {
        if (typeof col.show === 'function') {
          if (!col.show({ tab, view, isEvidence, source, referenceType: activeReferenceType })) {
            return false;
          }
        } else if (col.show !== undefined && !col.show) {
          return false;
        }

        // Hide the irrelevant title column in the grid when a reference-type filter is active.
        // Visibility remains user-controlled via the column selector.
        if (col.name === 'nct_title' || col.name === 'pmed_title') {
          const isAll = activeReferenceType === 'ALL' || !activeReferenceType;
          if (isAll) return true;
          if (col.name === 'nct_title') return activeReferenceType === 'NCTID';
          return activeReferenceType === 'PMID';
        }

        return true;
      }),
    [allColumns, tab, view, isEvidence, source, activeReferenceType],
  );
  const columns = columnsFromVisibility
    ? columnsFromVisibility.filter(col => columnsFilteredByShow.some(c => c.name === col.name))
    : null;

  const rowDetailColumnCatalog = useMemo(() => {
    if (!columnVisibilityModel || !Object.keys(columnVisibilityModel).length) {
      return [];
    }
    const modelKeyOrder = Object.keys(columnVisibilityModel);
    const inCurrentTable = (name: string) =>
      modelKeyOrder.includes(name) && (name !== 'sourceScore' || sourceWeights.length > 0);

    const allowed = columnsFilteredByShow.filter(col => inCurrentTable(col.name));
    const allowedNameSet = new Set(allowed.map(c => c.name));
    const fromUserOrder = (columnOrder ?? []).filter(id => allowedNameSet.has(id));
    const restByModel = modelKeyOrder.filter(n => allowedNameSet.has(n) && !fromUserOrder.includes(n));
    const ordered = [...fromUserOrder, ...restByModel].filter(name => name !== 'text');

    return ordered
      .map(name => {
        const col = allowed.find(c => c.name === name);
        if (!col) return null;

        const valueGetter =
          col.name === 'evidenceTimeline'
            ? (row: any) => {
                const yi = Number(row?.yearInitial);
                const yf = Number(row?.yearFinal);
                if (Number.isFinite(yi) && Number.isFinite(yf)) {
                  if (yi === 0 && yf === 0) return 'No publications';
                  return `${yi}–${yf}`;
                }
                const fr = Number(row?.firstRef);
                const lr = Number(row?.lastRef);
                if (Number.isFinite(fr) && Number.isFinite(lr)) {
                  if (fr === 0 && lr === 0) return 'No publications';
                  return `${fr}–${lr}`;
                }
                return null;
              }
            : undefined;

        return {
          id: col.name,
          displayLabel: extractHeaderText(col.header) || col.name,
          category: (col as any).category ?? '',
          ...(valueGetter ? { valueGetter } : {}),
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          displayLabel: string;
          category: string;
          valueGetter?: (row: any) => any;
        } => item != null,
      );
  }, [columnVisibilityModel, columnsFilteredByShow, columnOrder, sourceWeights]);

  const academicChemicalRestriction = (
    <AcademicRestrictions
      isAcademic={isAcademic}
      isAuthenticated={isAuthenticated}
      tab={tab}
      source={source}
      sourceCurated={sourceCurated}
      sourceOpen={false}
    />
  );

  if (isAcademic && isAuthenticated && tab === 'CHEMICALS') {
    return academicChemicalRestriction;
  }

  const mappingContextItems = isMapping
    ? (
        (mappingContent?.mappingItems as Array<{
          name?: string;
          id?: string;
          filterParam?: string;
          filterValue?: string;
        }>) ?? []
      ).filter(item => item?.name)
    : [];

  return (
    <SearchGridHelpContext.Provider value={searchGridHelpContextValue}>
      <>
        {/* Alert System */}
        {showAlert && (
          <div
            style={{
              position: 'fixed',
              bottom: '1.5rem',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}
          >
            <Alert
              message={getAlertMessage(
                saveOperations.isSavingSearch,
                saveOperations.isSavingColumns,
                false,
                saveOperations.isSavingScore,
              )}
              type={alertType}
              onClose={hideAlert}
            />
          </div>
        )}

        <div>
          {!isAuthenticated && view !== 'ALL' && (
            <p className="mt-4 text-pink">
              As an unregistered user you are viewing only 10 results ordered by score for each query. To view
              more, register for an account. Learn more about our <a href="/plans">plans here</a>.
            </p>
          )}
        </div>

        {/* Download Confirmation Modal */}
        {downloadOperations.showDownloadConfirm && (
          <SharedDialog
            isOpen={downloadOperations.showDownloadConfirm}
            onClose={() => downloadOperations.setShowDownloadConfirm(false)}
            title="Large file download warning"
            description="The file you are requesting contains more than 55,000 records. Downloads are limited to this number. We suggest using the REST API for this request."
            confirmText="Yes"
            cancelText="No"
            hasConfirmBtn
            onConfirm={downloadOperations.confirmLargeDownload}
            data-testid="modal"
          >
            <p className="font-medium text-gray-900 mt-2">Do you want to proceed?</p>
          </SharedDialog>
        )}

        {/* Save Modals */}
        <SaveSearchModal
          isOpen={saveOperations.showSaveSearchModal}
          onClose={() => {
            saveOperations.setShowSaveSearchModal(false);
            saveOperations.setSearchName('');
          }}
          onConfirm={saveOperations.confirmSaveSearch}
          searchName={saveOperations.searchName}
          setSearchName={saveOperations.setSearchName}
          searchDataToSave={saveOperations.searchDataToSave}
          isSaving={saveOperations.isSavingSearch}
        />

        <SaveColumnsModal
          isOpen={saveOperations.showSaveColumnsModal}
          onClose={() => {
            saveOperations.setShowSaveColumnsModal(false);
            saveOperations.setColumnSelectionName('');
          }}
          onConfirm={saveOperations.confirmSaveColumnSelection}
          columnSelectionName={saveOperations.columnSelectionName}
          setColumnSelectionName={saveOperations.setColumnSelectionName}
          columnDataToSave={saveOperations.columnDataToSave}
          isSaving={saveOperations.isSavingColumns}
        />

        <SaveScoreModal
          isOpen={saveOperations.showSaveScoreModal}
          onClose={() => {
            saveOperations.setShowSaveScoreModal(false);
            saveOperations.setScoreName('');
          }}
          onConfirm={saveOperations.confirmSaveScore}
          scoreName={saveOperations.scoreName}
          setScoreName={saveOperations.setScoreName}
          sourceWeights={sourceWeights}
          view={view}
          tab={tab}
          isSaving={saveOperations.isSavingScore}
        />

        {/* Main Content: table area (left info panel is rendered in SearchResults) */}
        <div className="w-full min-w-0">
          <div className="Cabtabla">
            {searchItems && (
              <>
                <div className="d-flex flex-wrap align-items-center justify-content-between w-100 gap-x-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
                    <RenderInfoLogic
                      searchItems={[]}
                      selectedInfoItem={selectedInfoItem}
                      setSearchItems={setSearchItems}
                      isMapping={isMapping}
                      showModalExample={false}
                      setShowModalExample={() => {}}
                      hideSearchItemsList
                    />
                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                      {summaryEvidenceToggle ? (
                        <SummaryEvidenceSegmentedControl
                          showSummary={summaryEvidenceToggle.showSummary}
                          showEvidence={summaryEvidenceToggle.showEvidence}
                          active={summaryEvidenceToggle.active}
                          onChange={summaryEvidenceToggle.onChange}
                        />
                      ) : (
                        <SharedBadge
                          text={isEvidence ? 'Evidence' : 'Summary'}
                          variant="secondary"
                          className="text-center justify-center h-7 mb-0"
                          size="md"
                        />
                      )}
                      <SharedBadge
                        text={source.replace('_', ' ')}
                        label="Source:"
                        variant="primaryLight"
                        className="text-center justify-center h-7 mb-0"
                      />
                    </div>
                  </div>

                  {mappingContextItems.length > 0 && (
                    <div className="d-flex flex-wrap items-center justify-center gap-1 min-w-0 flex-1 px-2">
                      {mappingContextItems.map((item, index) => {
                        const isGene = item.filterParam === 'gene_ncbi_id';
                        const geneNcbiId =
                          isGene && item.filterValue != null && String(item.filterValue).trim() !== ''
                            ? String(item.filterValue).trim()
                            : String(item.id ?? '').trim();
                        return (
                          <React.Fragment key={`${geneNcbiId}-${item.name ?? index}`}>
                            {index > 0 && <span className="px-1 text-slate-400">/</span>}
                            <span className="inline-flex items-center text-sm text-slate-800">
                              {item.name}
                              {isGene && onOpenSearchItemInfo && (
                                <MappedGeneInfoButton
                                  gene={{ id: geneNcbiId, name: item.name ?? '' }}
                                  onOpenPanel={onOpenSearchItemInfo}
                                />
                              )}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}

                  <div className="d-flex align-items-center justify-content-end gap-2 flex-shrink-0 w-full lg:w-auto">
                    <GridToolbar
                      sourceWeights={sourceWeights}
                      showSaveDropdown={showSaveDropdown}
                      setShowSaveDropdown={setShowSaveDropdown}
                      onSaveComplete={saveOperations.handleSaveComplete}
                      onSaveColumnSelection={saveOperations.handleSaveColumnSelection}
                      onSaveScore={saveOperations.handleSaveScore}
                      isSaving={saveOperations.isSavingSearch || saveOperations.isSavingColumns}
                      onResetToDefault={handleResetToDefault}
                      isAuthenticated={isAuthenticated}
                      isAcademic={isAcademic}
                      isTrial={isTrial}
                      view={view}
                      tab={tab}
                      isEvidence={isEvidence}
                      isMapping={isMapping}
                      mappingContent={mappingContent}
                      columnVisibilityModel={columnVisibilityModel}
                      columnOrder={columnOrder}
                      getFilterValueRef={getFilterValueRef}
                      searchItems={searchItems}
                      onDownload={downloadOperations.handleDownloadFilesWithFilters}
                      showEnrichmentTrigger={canShowEnrichmentSelection}
                      enrichmentSelectionMode={enrichmentSelectionMode}
                      onToggleEnrichmentSelection={handleToggleEnrichmentSelectionMode}
                      showScoreHistoryAction={
                        SHOW_HISTORIAL_SCORE && !isEvidence && (panelTab ?? tab).toUpperCase() === 'GDA'
                      }
                      onOpenScoreHistory={() =>
                        window.dispatchEvent(new CustomEvent('disgenet:open-score-history-from-actions'))
                      }
                    />
                    {academicChemicalRestriction}
                  </div>
                </div>
              </>
            )}
          </div>

          {canShowEnrichmentSelection && enrichmentSelectionMode && (
            <div className="enrichment-sticky-bar">
              <div className="enrichment-sticky-bar-content">
                <div className="enrichment-sticky-bar-left">
                  <span className="enrichment-selection-count">
                    {enrichmentSelectedCount} item{enrichmentSelectedCount !== 1 ? 's' : ''} selected
                  </span>
                  {enrichmentSelectedCount > 0 && !isRunEnrichmentEnabled(enrichmentSelectedCount) && (
                    <span className="enrichment-hint">Select at least 2 to run enrichment</span>
                  )}
                </div>
                <div className="enrichment-sticky-bar-actions">
                  <button
                    type="button"
                    onClick={handleToggleEnrichmentSelectionMode}
                    className="enrichment-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRunEnrichmentFromTable}
                    disabled={!isRunEnrichmentEnabled(enrichmentSelectedCount)}
                    title={
                      !isRunEnrichmentEnabled(enrichmentSelectedCount)
                        ? 'Select at least 2 items to run enrichment'
                        : 'Run enrichment with selected items'
                    }
                    className="enrichment-btn-primary"
                  >
                    Run enrichment
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Academic restrictions and Grid */}
          {isAcademic && !sourceCurated.includes(source) ? (
            <div className="restricted-wrapper w-full min-h-[240px] flex items-center justify-center py-8">
              <div className="restricted-box text-center">
                <LockClosedIcon className="mb-2 w-10 h-10" color="#D02591" aria-hidden />
                <h6>Your free academic plan includes curated sources only.</h6>
                <h6>
                  Select your available sources in the <span style={{ color: '#D02591' }}>Sources</span>{' '}
                  section of the control panel.
                </h6>
              </div>
            </div>
          ) : (
            <GridContainer
              key={defaultFilterValue ? JSON.stringify(defaultFilterValue) : 'default'}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
              columnPinning={columnPinning}
              setColumnPinning={setColumnPinning}
              view={view}
              source={source}
              tab={tab}
              panelTab={panelTab}
              searchItems={searchItems}
              isEvidence={isEvidence}
              isMapping={isMapping}
              mappingContent={mappingContent}
              setTotalResults={setTotalResults}
              onData={handleDataFromChild}
              getFilterValueRef={getFilterValueRef}
              defaultFilterValue={defaultFilterValue}
              sourceWeights={sourceWeights}
              isInvalidSource={isInvalidSource}
              rowDetailColumnCatalog={rowDetailColumnCatalog}
              enableEnrichmentSelection={enableEnrichmentSelection}
              getRowIdForEnrichment={canShowEnrichmentSelection ? getRowIdForEnrichment : undefined}
              enrichmentGetSelectionRef={canShowEnrichmentSelection ? enrichmentGetSelectionRef : undefined}
              enrichmentRestoreSelection={enrichmentRestoreSelection}
              onEnrichmentRestoreApplied={() => setEnrichmentRestoreSelection(null)}
              onFiltersChange={handleFiltersChange}
              showSummaryMatchBar={isEvidence && showSummaryMatch && hasSpecificIdents && hasActiveFilters}
              onOpenSummaryMatch={summaryMatch.openSummaryMatch}
              isSummaryMatchLoading={summaryMatch.isLoading}
              summaryMatchEncodedApiParams={
                !isEvidence && summaryMatch.isActive ? summaryMatch.matchEncodedParams : null
              }
              summaryMatchSessionKey={
                !isEvidence && summaryMatch.isActive ? summaryMatch.matchSessionKey : null
              }
              summaryMatchActive={!isEvidence && summaryMatch.isActive}
              summaryMatchStats={summaryMatch.stats}
              summaryMatchPreserveFilters={
                !isEvidence && summaryMatch.isActive && summaryMatch.preserveFilters
              }
              onClearSummaryMatch={handleClearSummaryMatch}
            />
          )}

          {/* Evidence legend */}
          {isEvidence && (
            <div className="flex justify-between mt-2">
              {EVIDENCE_LEGEND}
              {areEvidenceSelectedRows && SHOW_SUMMARIZE && (
                <div>
                  <SharedButton onClick={handleEvidenceSummClick}>Summarize</SharedButton>
                </div>
              )}
            </div>
          )}

          {showRightSidebar && (
            <RightAsideSidebar
              isOpen={isSidebarOpen}
              onClose={() => setSidebarOpen(false)}
              source={source}
              sourceWeights={sourceWeights}
              view={view}
              tab={tab}
              isEvidence={isEvidence}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              searchItems={searchItems}
              onOpenSearchItemInfo={onOpenSearchItemInfo}
              onRemoveSearchItem={handleRemoveSearchItem}
              onClearAllSearchItems={() => {
                setSearchItems([]);
                navigate('/');
              }}
              isMapping={isMapping}
              onApplySavedColumns={handleApplySavedColumns}
              onDeleteSavedColumns={handleDeleteSavedColumn}
              mappingContent={mappingContent}
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
              isAcademic={isAcademic}
              onResetToDefault={handleResetToDefault}
            />
          )}
        </div>
        <SearchGridHelpModal
          open={searchHelpOpen}
          setOpen={next => {
            setSearchHelpOpen(next);
            if (!next) {
              setSearchHelpScrollToSectionId(undefined);
            }
          }}
          scrollToSectionId={searchHelpScrollToSectionId}
        />
      </>
    </SearchGridHelpContext.Provider>
  );
}

export default ManageGrid;
